import { useState, useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import Preloader from "./components/Preloader";

// ✅ Layouts
import AdminLayout from "./layouts/AdminLayout";
import AdminProtectedRoute from "./components/dashboard/AdminProtectedRoute";

// ✅ Public Pages — Lazy (Services Only)
const Home = lazy(() => import("./pages/Home"));
const Contact = lazy(() => import("./pages/Contact"));
const Services = lazy(() => import("./pages/Services"));
const BridalService = lazy(() => import("./pages/services/BridalService"));
const EventsService = lazy(() => import("./pages/services/EventsService"));
const HairService = lazy(() => import("./pages/services/HairService"));
const EditorialService = lazy(
  () => import("./pages/services/EditorialService"),
);
const MasterclassesService = lazy(
  () => import("./pages/services/MasterclassesService"),
);
const Gallery = lazy(() => import("./pages/Gallery"));
const About = lazy(() => import("./pages/About"));
const Product = lazy(() => import("./pages/Products"));
const FAQs = lazy(() => import("./pages/FAQs"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Error404 = lazy(() => import("./pages/404"));

// ✅ Admin Pages — Lazy
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const Bookings = lazy(() => import("./pages/admin/Bookings"));
const BookingDetail = lazy(() => import("./pages/admin/BookingDetail"));
const Enquiries = lazy(() => import("./pages/admin/Enquiries"));
const Customers = lazy(() => import("./pages/admin/Customers"));
const CustomerDetail = lazy(() => import("./pages/admin/CustomerDetail"));
const Profile = lazy(() => import("./pages/admin/Profile"));
const ActivityLogs = lazy(() => import("./pages/admin/ActivityLogs"));
const Roles = lazy(() => import("./pages/admin/RoleManagement"));
const CategoryList = lazy(() => import("./pages/admin/CategoryList"));
const EditCategory = lazy(() => import("./pages/admin/EditCategory"));
const AddCategory = lazy(() => import("./pages/admin/AddCategory.js"));
const AddProduct = lazy(() => import("./pages/admin/AddProduct.js"));
const ProductsList = lazy(() => import("./pages/admin/productList.js"));
const EditProducts = lazy(() => import("./pages/admin/EditProducts.js"));
const OrderList = lazy(() => import("./pages/admin/OrderList.js"));
const OrderDetail = lazy(() => import("./pages/admin/OrderDetail.js"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const AdminProductReviews = lazy(
  () => import("./pages/admin/AdminProductReviews.js"),
);
const ReportsDashboard = lazy(
  () => import("./pages/admin/ReportsDashboard.js"),
);
const InvoiceList = lazy(() => import("./pages/admin/InvoiceList.js"));
const InvoiceView = lazy(() => import("./pages/admin/InvoiceView.js"));

// ✅ AppWrapper
function AppWrapper() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/admin");

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      if (
        event.reason === "Timeout" ||
        event.reason?.message?.includes("Timeout")
      ) {
        event.preventDefault();
        console.warn("Caught Google reCAPTCHA timeout silently.");
      }
    };
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () =>
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
  }, []);

  useEffect(() => {
    const isReactSnap = navigator.userAgent === "ReactSnap";
    const timer = setTimeout(() => setLoading(false), isReactSnap ? 0 : 2000);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <Preloader />;
  }

  return (
    <>
      {!isDashboard && <Header />}

      <Suspense
        fallback={<div style={{ height: "100vh", background: "#1a1209" }} />}
      >
        <Routes>
          {/* ✅ Services Public Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/bridal-makeup" element={<BridalService />} />
          <Route path="/services/events-makeup" element={<EventsService />} />
          <Route path="/services/hair" element={<HairService />} />
          <Route
            path="/services/editorial-makeup"
            element={<EditorialService />}
          />
          <Route
            path="/services/masterclasses"
            element={<MasterclassesService />}
          />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/products" element={<Product />} />
          <Route path="/about" element={<About />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/terms-and-conditions" element={<TermsOfUse />} />

          {/* ✅ Services Admin Only */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route
              index
              element={
                <AdminProtectedRoute permission="dashboard.view">
                  <AdminDashboard />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="bookings"
              element={
                <AdminProtectedRoute permission="bookings.view">
                  <Bookings />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="bookings/:id"
              element={
                <AdminProtectedRoute permission="bookings.view">
                  <BookingDetail />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="enquiries"
              element={
                <AdminProtectedRoute permission="enquiries.view">
                  <Enquiries />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="categories"
              element={
                <AdminProtectedRoute permission="categories.view">
                  <CategoryList />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="categories/create"
              element={
                <AdminProtectedRoute permission="categories.create">
                  <AddCategory />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="categories/:id"
              element={
                <AdminProtectedRoute permission="categories.update">
                  <EditCategory />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="products"
              element={
                <AdminProtectedRoute permission="products.view">
                  <ProductsList />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="products/create"
              element={
                <AdminProtectedRoute permission="products.create">
                  <AddProduct />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="products/edit/:id"
              element={
                <AdminProtectedRoute permission="products.update">
                  <EditProducts />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="customers"
              element={
                <AdminProtectedRoute allow={["super_admin"]}>
                  <Customers />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/customers/:email"
              element={
                <AdminProtectedRoute allow={["super_admin"]}>
                  <CustomerDetail />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="orders"
              element={
                <AdminProtectedRoute permission="orders.view">
                  <OrderList />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="orders/:id"
              element={
                <AdminProtectedRoute permission="orders.update_status">
                  <OrderDetail />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="profile"
              element={
                <AdminProtectedRoute allow={["super_admin"]}>
                  <Profile />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <AdminProtectedRoute allow={["super_admin"]}>
                  <ReportsDashboard />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="invoices"
              element={
                <AdminProtectedRoute allow={["super_admin"]}>
                  <InvoiceList />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="invoices/:id"
              element={
                <AdminProtectedRoute allow={["super_admin"]}>
                  <InvoiceView />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="role"
              element={
                <AdminProtectedRoute permission="roles.view">
                  <Roles />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="user-management"
              element={
                <AdminProtectedRoute permission="admins.view">
                  <UserManagement />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="activity"
              element={
                <AdminProtectedRoute allow={["admin", "super_admin"]}>
                  <ActivityLogs />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/reviews"
              element={
                <AdminProtectedRoute allow={["admin", "super_admin"]}>
                  <AdminProductReviews />
                </AdminProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Error404 />} />
        </Routes>
      </Suspense>

      {!isDashboard && <Footer />}
    </>
  );
}

// ✅ App
function App() {
  return (
    <HelmetProvider>
      <Router>
        <ErrorBoundary>
          <AppWrapper />
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  );
}

export default App;
